import { useCallback, useState } from 'react';
import useAsync from '@dyl/hooks/useAsync';
import useMemoObject from '@dyl/hooks/useMemoObject';
import { getAllTokens, getToken } from '@dyl/apiClients/backendApi';
import useExport from './useExport';
import { difference, size, union, without } from 'lodash';
import { useSnackbar } from 'notistack';
import useTokenDisable from './useTokenDisable';
import useResultsJsonDownload from './useResultsJsonDownload';
import getResults from '@dyl/apiClients/backendApi/getResults';
import downloadUrl from '@dyl/utils/downloadUrl';
import deriveBackendErrorMessage from '@dyl/utils/deriveBackendErrorMessage';
import { useLevels } from "@dyl/systems/LevelsManager";
import calculateSpeakingResult from "@dyl/apiClients/backendApi/calculateSpeakingResult";

export default function useLogic() {
    const { globalLevel, levels } = useLevels();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [intendedSelectedIds, setIntendedSelectedIds] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const exportLimit = 500;

    const [sortModel, _setSortModel] = useState(
        /** @returns {import('@mui/x-data-grid').GridSortModel} */
        () => [{ field: 'created_at', sort: 'desc' }]
    );

    /**
     * Wrapper to ensure there is always some kind of sort, as the API
     *  requires one, and the list in turn makes no sense without one.
     */
    const setSortModel = useCallback(
        /** @param {import('@mui/x-data-grid').GridSortModel} next */
        next => {
            _setSortModel(([prev]) => {
                // If the sort would be disabled, flip the previous sort instead.
                if (!next?.length) {
                    return [{
                        ...prev,
                        sort: prev.sort === 'desc' ? 'asc' : 'desc'
                    }];
                }

                return next;
            });
        }
    );

    const [filterParams, setFilterParams] = useState({});

    /** @type {Parameters<getAllTokens>[0]} */
    const queryParams = useMemoObject({
        orderBy: sortModel[0]?.field ?? 'created_at',
        order: (sortModel[0]?.sort ?? 'desc').toUpperCase(),
        page: page + 1,
        perPage: pageSize,
        ...filterParams,
    });

    const [isUpdatingList, setUpdatingList] = useState(true);

    const [_listIntegrity, _setListIntegrity] = useState(0);
    const forceRefreshList = useCallback(() => {
        _setListIntegrity(prev => prev + 1);
    }, []);

    const filterExistingSelection = (currentIds, tokens, intendedSelectedIds) => {
        if (!tokens.length || !intendedSelectedIds.length) return currentIds;

        const existingSelection = currentIds.filter((id) =>
            tokens.some((token) => token.token_id === id)
        );

        return existingSelection;
    };

    let { tokens = [], countAll = 0, countFiltered = 0 } = useAsync(async ({ checkValid, prev }) => {
        setUpdatingList(true);
        await new Promise(resolve => setTimeout(resolve, 400));
        if (!checkValid()) return prev;

        setSelectedIds((currentIds) => filterExistingSelection(currentIds, tokens, intendedSelectedIds));

        try {
            const res = await getAllTokens(globalLevel.level_id, queryParams);
            if (!res.countAll) res.countAll = res.tokens.length;
            if (!res.countFiltered) res.countFiltered = res.tokens.length;

            return res;
        }
        catch {
            enqueueSnackbar('Failed to get password page.', { variant: 'error' });
        }
        finally {
            setUpdatingList(false);
        }

    }, [globalLevel, queryParams, _listIntegrity, intendedSelectedIds]) ?? {};

    const [searchId, setSearchId] = useState('');
    const [isSearching, setSearching] = useState(false);

    const searchedToken = useAsync(async ({ checkValid, prev }) => {
        // Only request when valid ID format is entered

        const isValidTokenId = /^[0-9]{4}-[0-9]{4}-[0-9]{4}$/.test(searchId) || /^\d{5}-\d{5}-\d{5}$/.test(searchId);
        const isValidBPNumber = /^(0?[0-9]{9})$/.test(searchId);
        if (!isValidTokenId && !isValidBPNumber) return null;

        setSearching(true);

        // Wait a short moment, in case there are rapid changes in request parameters.
        await new Promise(resolve => setTimeout(resolve, 400));
        if (!checkValid()) return prev;

        let token = null;
        try {
            token = await getToken(globalLevel.level_id, searchId);
            if (globalLevel.level_id === token.level_id) {
                setSelectedIds([token.token_id]);
                return token;
            } else {
                const token_level = levels.find(l => l.level_id === token.level_id);
                if (!token_level) {
                    enqueueSnackbar(`Invalid password, please report this issue.`, { variant: 'error' });
                } else {
                    enqueueSnackbar(`The password with given ID belongs to ${token_level.label} level.`, { variant: 'warning' });
                }
            }
        }

        finally { setSearching(false); }
    }, [searchId]);

    if (searchId) {
        tokens = searchedToken ? [searchedToken] : [];
        countFiltered = searchedToken ? 1 : 0;
    }

    const handleSelectionChange = useCallback(
        (newSelection) => {
            setIntendedSelectedIds(newSelection);
        },
        [setIntendedSelectedIds]
    );

    const [isUpdatingSelection, setUpdatingSelection] = useState(false);

    const selectAll = useCallback(async () => {
        // If searching, select just that (if possible)
        if (searchId) {
            if (searchedToken) {
                setSelectedIds(prev => union(prev, [searchId]));
            }

            return;
        }

        setUpdatingSelection(true);
        setUpdatingSelection(false);
    }, [filterParams, searchId, searchedToken]);

    const selectNone = useCallback(async () => {
        // If not filtered, clear selection entirely.
        if (size(filterParams) === 0) {
            return setSelectedIds([]);
        }

        // If searching, remove just that
        if (searchId) {
            return setSelectedIds(prev => without(prev, searchId));
        }

        // Otherwise, deselect only filtered tokens
        setUpdatingSelection(true);

        try {
            const { tokens } = await getAllTokens(globalLevel.level_id, filterParams);
            const ids = tokens.map(t => t.token_id);

            setSelectedIds(prev => difference(prev, ids));
        }
        catch {
            enqueueSnackbar('Failed to deselect filtered passwords.', { variant: 'error' });
        }
        finally {
            setUpdatingSelection();
        }
    }, [filterParams]);

    // EXPORT TOKEN(S)
    const [isExporting, setExporting] = useState(false);
    const exportTokens = useExport();

    const onExportClick = async () => {
        let indicatorId;
        if (selectedIds.length === 0) {
            return enqueueSnackbar('Please Select Atleast One Password To Export', { variant: 'error' });
        }

        const selectedForExport = selectedIds.slice(0, exportLimit);

        if (selectedForExport.length < selectedIds.length) {
            return enqueueSnackbar(`Only ${exportLimit} passwords can be exported at once.`, { variant: 'error' });
        } else {
            indicatorId = enqueueSnackbar(`Exporting ${selectedIds.length} passwords`, {
                variant: 'info',
                persist: true,
            });
        }

        setExporting(true);

        try { await exportTokens(selectedIds, globalLevel.level_id, filterParams); }
        finally {
            setExporting(false);
            closeSnackbar(indicatorId);
        }
    };

    // DISABLE TOKEN(S)
    const [isDisabling, setDisabling] = useState(false);
    const disableTokens = useTokenDisable();

    const onDisableClick = async () => {
        if (selectedIds.length === 0) {
            return enqueueSnackbar('No passwords selected', { variant: 'error' });
        }

        const indicatorId = enqueueSnackbar(`Disabling ${selectedIds.length} passwords`, {
            variant: 'info',
            persist: true,
        });

        setDisabling(true);

        try {
            await disableTokens(selectedIds,globalLevel.level_id);
            forceRefreshList();

            enqueueSnackbar('Successfully disabled passwords.', { variant: 'success' });
        }
        catch {
            enqueueSnackbar('Failed to disable passwords.', { variant: 'error' });
        }
        finally {
            setDisabling(false);
            closeSnackbar(indicatorId);
        }
    };

    // JSON RESULTS DOWNLOAD
    const [isDownloadingJson, setDownloadingJson] = useState(false);
    const downloadJson = useResultsJsonDownload(tokens);

    const onDownloadJsonClick = async () => {
        setDownloadingJson(true);

        const indicatorId = enqueueSnackbar(`Downloading JSON results for ${selectedIds[0]}`, {
            variant: 'info',
            persist: true,
        });

        try {
            await downloadJson(selectedIds);
        }
        finally {
            setDownloadingJson(false);
            closeSnackbar(indicatorId);
        }
    }

    const [isDownloadingCsv, setDownloadingCsv] = useState(false);
    const onDownloadCsvClick = async () => {

        if (selectedIds.length === 0) {
            return enqueueSnackbar('No passwords selected', { variant: 'error' });
        }

        const selectedForExport = selectedIds.slice(0, exportLimit);

        if (selectedForExport.length < selectedIds.length) {
            return enqueueSnackbar(`Only ${exportLimit} results can be exported at once.`, { variant: 'error' });
        }

        const indicatorId = enqueueSnackbar(`Exporting results for ${selectedIds.length} passwords`, {
            variant: 'info',
            persist: true,
        });

        setDownloadingCsv(true);

        const { url } = await (async () => {
            try {
                return await getResults(selectedIds);
            }
            catch (error) {
                enqueueSnackbar(deriveBackendErrorMessage(error), { variant: 'error' });
            }
            finally {
                closeSnackbar(indicatorId);
                setDownloadingCsv(false);
            }
        })();

        downloadUrl('Results.csv', url);
    };

    const [isCalculatingSpeakingResult, setCalculatingSpeakingResult] = useState(false);
    const onCalculateSpeakingResult = async(user_id) =>  {

        setCalculatingSpeakingResult(true);

        try {
            const { success } = await calculateSpeakingResult(user_id);
            if (success) {
                enqueueSnackbar(`Calculation complete`, { variant: 'success' });
            } else {
                enqueueSnackbar('An unknown error occurred while trying to re-calculate, please check the web logs or contact your administrator.', { variant: 'error' });
            }
        } catch (error) {
            const message = deriveBackendErrorMessage(error);
            enqueueSnackbar(message, { variant: 'error' });
        }

        setCalculatingSpeakingResult(false);
    };


    return {
        isLoading: isUpdatingList || isUpdatingSelection || isSearching,
        tokens,

        selectedIds, setSelectedIds,

        isExporting, onExportClick,
        isDisabling, onDisableClick,
        isDownloadingJson, onDownloadJsonClick,
        isDownloadingCsv, onDownloadCsvClick,
        isCalculatingSpeakingResult, onCalculateSpeakingResult,

        countFiltered, countAll,
        pageSize, setPageSize,
        page, setPage,
        sortModel, setSortModel,
        selectAll, selectNone,

        filterParams, setFilterParams,
        searchId, setSearchId,
        handleSelectionChange,
    };
}